<template>
    <not-found-view v-if="error" :error500="true"></not-found-view>
    
    <div v-else class="grid grid--padding">
        <div class="results">
            <h2 class="results__title">{{ $t('game-results.title') }}</h2>

            <div class="results__container scroll-reveal animate__animated" data-animation="bounceIn">
                <div class="results__subtitle">{{ $t('game-results.score-number') }}: {{ savedResult.score }}</div>
                <img :src="vectaryModelScreenshot" :alt="$t('game-results.your-house-text')" class="results__img" />
                <div v-if="scoreMessage" class="results__text">{{ scoreMessage }}</div>
                <button @click="shareToFacebook" class="btn btn--facebook">
                    <img src="@/assets/images/facebook-icon.svg" :alt="$t('button.facebook-share')" />
                    {{ $t('button.facebook-share') }}
                </button>
            </div>

            <div class="scroll-reveal animate__animated" data-animation="wobble">
                <div :class="['results__nav', {'results__nav--left': selectedTab == 'results-during-building', 'results__nav--right': selectedTab == 'results-after-building'}]">
                    <button @click="selectTab('results-during-building')" class="results__tab">{{ $t('game-results.tab.during-building') }}</button>
                    <button @click="selectTab('results-after-building')" class="results__tab">{{ $t('game-results.tab.after-building') }}</button>
                </div>
            </div>

            <div v-if="isTooltipVisible" class="results__tooltip scroll-reveal animate__animated" data-animation="bounceIn">
                <h3 class="results__tooltip-title">{{ $t('game-results.tooltip.title') }}</h3>
                <vueper-slides @slide="checkSlide($event)" :infinite="false" :disableArrowsOnEdges="true" class="no-shadow">
                    <vueper-slide v-for="(tooltip, index) in tooltipContent" :key="index" :content="tooltip" />

                    <template #arrow-right>
                        <div class="btn btn--primary">{{ $t('button.next') }}</div>
                    </template>
                    <template #arrow-left>
                        <button v-if="isTooltipCloseButtonVisible" @click.stop="closeTooltip" class="btn btn--primary">{{ $t('button.close') }}</button>
                        <div class="btn">{{ $t('button.back') }}</div>
                    </template>
                </vueper-slides>
            </div>

            <keep-alive>
                <component :is="selectedTab"></component>
            </keep-alive>

            <router-link :to="{name: 'quiz'}" class="btn btn--quiz">
                <svg width="33" height="33" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.8259 26.7964C17.5264 26.2777 17.4458 25.6713 17.5989 25.0886C17.5992 25.0871 17.5997 25.0853 17.6001 25.0837C17.6483 24.9061 17.7006 24.7124 17.7489 24.5326C17.8256 24.2472 17.6983 23.9441 17.438 23.8044C17.0197 23.5801 16.498 23.5704 16.0576 23.8247L13.0878 25.5393C12.7815 25.7161 12.3899 25.6112 12.2131 25.3049L5.01157 12.8315C4.83473 12.5253 4.93965 12.1337 5.24594 11.9568L8.21576 10.2422C8.65621 9.98793 9.17789 9.9977 9.59619 10.222C9.85649 10.3617 9.98376 10.6648 9.90705 10.9502C9.85875 11.13 9.8065 11.3236 9.75829 11.5012C9.75786 11.5028 9.75729 11.5045 9.75709 11.5062C9.60403 12.0888 9.68458 12.6952 9.98398 13.2138C10.7202 14.489 12.3417 14.9403 13.6286 14.2425C13.6505 14.2314 13.6721 14.2198 13.6935 14.2074L14.8506 13.5393C14.872 13.527 14.8929 13.5141 14.9135 13.5007C16.1612 12.7351 16.5811 11.1052 15.8449 9.83002C15.5456 9.31158 15.0607 8.93838 14.4796 8.77962C14.4779 8.77905 14.4764 8.77862 14.4746 8.77828C14.2968 8.73113 14.103 8.67956 13.923 8.63157C13.6386 8.55548 13.4399 8.29509 13.4478 8.00072C13.4507 7.89125 13.4664 7.78205 13.4951 7.67574C13.5868 7.33091 13.7981 7.04099 14.0944 6.85011C14.1147 6.83783 14.1344 6.82507 14.155 6.81323L17.7193 4.75535C18.0256 4.57851 18.4172 4.68343 18.594 4.98972L20.6516 8.5535C20.8027 8.81532 21.2285 9.25191 21.8338 9.26824C22.1294 9.27627 22.3915 9.0793 22.4682 8.79394C22.5159 8.61604 22.5674 8.42416 22.6152 8.24677C22.7742 7.65571 23.1528 7.16217 23.6813 6.85702C24.9893 6.10188 26.633 6.56809 27.3708 7.84598L28.0464 9.01614C28.795 10.3127 28.3489 11.9766 27.0515 12.7256C26.5284 13.0276 25.9216 13.1071 25.342 12.9493C25.1631 12.902 24.9683 12.8501 24.7878 12.8018C24.5025 12.7256 24.2 12.8529 24.0606 13.1131C23.8363 13.5314 23.8267 14.0527 24.081 14.4932L25.7957 17.463C25.9725 17.7693 25.8674 18.1609 25.5613 18.3377L21.9968 20.3957C21.6687 20.5851 21.4346 20.8904 21.337 21.2582C21.3084 21.3644 21.2927 21.4737 21.2897 21.5832C21.2818 21.8777 21.4804 22.1379 21.7649 22.2139C21.9448 22.262 22.1386 22.3136 22.3164 22.3607C22.3183 22.3612 22.3199 22.3616 22.3214 22.3621C22.9026 22.5208 23.3874 22.894 23.6868 23.4126C24.4354 24.7091 23.9893 26.373 22.6925 27.1218L21.5354 27.7898C20.2385 28.5386 18.5744 28.0929 17.8259 26.7964V26.7964Z" stroke="white" stroke-width="1.5"/>
                </svg>
                <span>{{ $t('button.challenge') }}</span>
            </router-link>

            <div class="btn-wrapper">
                <button @click="toggleModal" class="btn btn--primary">{{ $t('button.i-want-to-try-again') }}</button>
            </div>

            <modal-component v-if="isModalVisible">
                <template #title>{{ $t('game-results.try-again-text') }}</template>

                <template #content>
                    <img src="@/assets/images/try-again.svg" :alt="$t('game-results.try-again-text')" />

                    <template v-if="!savedResult.name">
                        <div class="modal__text">{{ $t('game-results.result-is-not-saved-text') }}</div>

                        <ol class="leaderboard__list">
                            <li class="leaderboard__item leaderboard__item--highlighted">
                                <span>{{ savedResult.rank }}. {{ $t('game-leaderboard.you-are-here') }}</span>
                                <span>{{ savedResult.score }}</span>
                            </li>
                        </ol>

                        <add-result-form @name-submit="saveResultAndResetGame" :buttonText="'button.publish-result-and-play-again'" :error="formError"></add-result-form>
                    </template>
                </template>

                <template #actions>
                    <button v-if="savedResult.name" @click="resetGame" class="btn btn--primary">{{ $t('button.yes-try-again') }}</button>
                    <button v-else @click="resetGame" class="btn btn--primary">{{ $t('button.try-again') }}</button>
                    <button @click="toggleModal" class="btn">{{ $t('button.back-to-results') }}</button>
                </template>
            </modal-component>

            <div class="results__canvas">
                <canvas id="fb-share-image" class="fb-share-canvas" width="854" height="640"></canvas>
            </div>
        </div>
    </div>
</template>

<script>
import { computed } from 'vue';
import { VueperSlides, VueperSlide } from 'vueperslides';
import 'vueperslides/dist/vueperslides.css';

import { getBuildReport } from '@/queries/buildReportQueries.js';
import { updateBuildReport } from '@/queries/gameLeaderboardQueries.js';
import { createAsset } from '@/queries/assetQueries.js';
import ResultsDuringBuilding from '@/components/game/ResultsDuringBuilding.vue';
import ResultsAfterBuilding from '@/components/game/ResultsAfterBuilding.vue';
import ModalComponent from '@/components/ModalComponent.vue';
import AddResultForm from '@/components/game/AddResultForm.vue';

export default {
    name: 'GameResultsView',
    components: {
        VueperSlides,
        VueperSlide,
        ResultsDuringBuilding,
        ResultsAfterBuilding,
        ModalComponent,
        AddResultForm
    },
    inject: ['scrollTrigger', 'addObserver'],
    data() {
        return {
            error: false,
            formError: false,
            getBuildReport: null,
            selectedTab: 'results-during-building',
            isTooltipVisible: true,
            isTooltipCloseButtonVisible: false,
            tooltipContent: [
                this.$t('game-results.tooltip.instruction-text'),
                this.$t('game-results.tooltip.during-building-text'),
                this.$t('game-results.tooltip.after-building-text'),
            ],
            isModalVisible: false
        }
    },
    provide() {
        return {
            buildReport: computed(() => this.getBuildReport)
        }
    },
    apollo: {
        getBuildReport: {
            query: getBuildReport,
            variables() {
                return {
                    id: parseInt(this.savedResult.id)
                }
            },
            error(error) {
                console.log('errors', error);
                this.error = true;
            }
        },
    },
    computed: {
        vectaryModelScreenshot() {
            return this.$store.getters['game/getVectaryModelScreenshot'];
        },
        savedResult() {
            return this.$store.getters['game/getSavedResult'];
        },
        apiUri() {
            return process.env.VUE_APP_APOLLO_API_URI;
        },
        assetFolder() {
            return process.env.VUE_APP_ASSET_FOLDER;
        },
        scoreMessage() {
            if (this.getBuildReport && this.getBuildReport.ScoreMessage) {
                return this.getBuildReport.ScoreMessage[0].message;
            } else {
                return false;
            }
        }
    },
    methods: {
        selectTab(tab) {
            this.selectedTab = tab;
        },
        closeTooltip() {
            this.isTooltipVisible = false;
        },
        checkSlide(params) {
            console.log(params.currentSlide.index, this.tooltipContent.length);
            if (params.currentSlide.index + 1 == this.tooltipContent.length) {
                this.isTooltipCloseButtonVisible = true;
            } else {
                this.isTooltipCloseButtonVisible = false;
            }
        },
        toggleModal() {
            this.isModalVisible = !this.isModalVisible;
            document.body.classList.toggle('no-scroll');
        },
        resetGame() {
            this.$store.dispatch('game/resetGame');
        },
        saveResultAndResetGame(name) {
            const resultData = {
                rank: this.savedResult.rank,
                name: name,
                score: this.savedResult.score
            }

            this.$apollo.mutate({
                mutation: updateBuildReport,
                variables: {
                    id: parseInt(this.savedResult.id),
                    name: resultData.name,
                    published: true
                }
            }).then((res) => {
                if (res.data.updateBuildReport.output.ResultPublished) {
                    this.$store.commit('game/saveResult', resultData);
                    this.$store.dispatch('game/resetGame');
                }
            }).catch((error) => {
                console.error(error);
                this.formError = true;
            });
        },
        shareToFacebook() {
            const canvas = document.getElementById("fb-share-image");
            const canvasImage = canvas.toDataURL("image/png");

            this.$apollo.mutate({
                mutation: createAsset,
                variables: {
                    filename: `model-${this.savedResult.id}-${Date.now()}.png`,
                    imageData: canvasImage.split(',').pop(),
                    assetFolder: parseInt(this.assetFolder)
                }
            }).then((res) => {
                if (res.data.createAsset.success) {
                    window.FB.ui({
                        display: 'popup',
                        method: 'share',
                        media: [`${this.apiUri}${res.data.createAsset.assetData.fullpath}`],
                    }, function(){});
                }
            }).catch((error) => {
                console.error(error);
            });
        }
    },
    mounted() {
        const canvas = document.getElementById("fb-share-image");
        const ctx = canvas.getContext("2d");

        const bg = new Image();
        bg.onload = function() {
            ctx.drawImage(bg, 0, 0, 854, 640);
        }
        bg.src = require('@/assets/images/fb-share-background.png');

        const img = new Image();
        img.onload = function() {
            ctx.drawImage(img, 0, 0, 854, 640);
        }
        img.src = this.vectaryModelScreenshot;

        const overlay = new Image();
        overlay.onload = function() {
            ctx.drawImage(overlay, 0, 0, 854, 640);
        }
        overlay.src = require('@/assets/images/fb-share-overlay.png');

        this.scrollTrigger('.scroll-reveal', {
            rootMargin: '-50px'
        });
    }
}
</script>
